import { useState } from 'react';
import type { AnswerDecisionBody } from '@ni/shared';
import { useResuming } from '../../api/app-context';
import { useAnswerDecision, useDecision } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf, loadErrorLine } from '../../api/errors';
import { CandidateCard } from './candidate-card';
import { CandidateList } from './candidate-list';
import { CollapsedBin } from '../ui/collapsed-bin';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import {
  SAME_PERSON_ASK,
  SET_ASIDE_BIN_OPEN_LABEL,
  setAsideBinLabel,
  setAsideReason,
} from '../../lib/copy';
import { SamePersonFoot, SamePersonOpen, useSamePerson } from './same-person';
import { Loading } from '../ui/loading';
import { EmptyLine } from '../ui/empty-line';

/**
 * The pick list (ui-design §4.5), rendered in **server order** — qualitative
 * binding strength, never re-sorted, never scored. Every card's Confirm is live;
 * the demo's inert non-best cards were staging.
 *
 * Same-person mode (`same-person.tsx`) swaps each Confirm for a checkbox.
 */

/**
 * What is in flight: one card, a linked group (by its first ref), the
 * same-person answer, or None of these. Tagged rather than a string keyspace,
 * so a card whose ref happened to read like one of the other keys cannot put the
 * wrong button into its working state (as `more-prose.tsx`).
 */
type Pending =
  | { kind: 'card'; ref: string }
  | { kind: 'group'; ref: string }
  | { kind: 'same' }
  | { kind: 'none' }
  | null;

const isCard = (pending: Pending, ref: string): boolean =>
  pending?.kind === 'card' && pending.ref === ref;

export function PickList({
  subjectId,
  decisionId,
  runId,
  runState,
}: {
  subjectId: string;
  decisionId: string;
  runId: string;
  runState: 'awaiting_identity_pick';
}) {
  const decision = useDecision(decisionId);
  const answer = useAnswerDecision(subjectId);
  const { markResuming } = useResuming();
  const [pending, setPending] = useState<Pending>(null);
  const samePerson = useSamePerson();

  if (decision.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (decision.isError) return <EmptyLine>{loadErrorLine(decision.error)}</EmptyLine>;
  if (decision.data.kind !== 'identity_pick') return null;

  const payload = decision.data.payload;
  const standing = standingAnswerOf(answer.error);

  const submit = (next: NonNullable<Pending>, body: AnswerDecisionBody) => {
    setPending(next);
    answer.mutate(
      { decisionId, body, runId },
      {
        onSuccess: () => markResuming(runId, runState),
        onSettled: () => setPending(null),
      },
    );
  };

  return (
    <div className="run-under">
      {standing ? <div className="src">{standingAnswerLine(standing)}</div> : null}
      {answer.isError && !standing ? <ErrorLine error={answer.error} /> : null}

      {samePerson.checked ? <div className="src">{SAME_PERSON_ASK}</div> : null}

      <CandidateList
        candidates={payload.candidates}
        card={(candidate, onCorrectionsChange) => (
          <CandidateCard
            candidate={candidate}
            working={isCard(pending, candidate.candidate_ref)}
            disabled={pending !== null && !isCard(pending, candidate.candidate_ref)}
            onConfirm={(corrections) =>
              submit(
                { kind: 'card', ref: candidate.candidate_ref },
                {
                  chosen_candidate: candidate.candidate_ref,
                  ...(corrections.length > 0 ? { anchor_corrections: corrections } : {}),
                },
              )
            }
            selection={samePerson.selectionFor(candidate.candidate_ref)}
            onCorrectionsChange={onCorrectionsChange}
          />
        )}
        onConfirmGroup={(refs, corrections) =>
          submit(
            { kind: 'group', ref: refs[0] as string },
            {
              chosen_candidates: refs,
              ...(corrections.length > 0 ? { anchor_corrections: corrections } : {}),
            },
          )
        }
        workingGroup={pending?.kind === 'group' ? pending.ref : null}
        disabled={pending !== null}
        selecting={samePerson.checked !== null}
      />

      {payload.suppressed.length > 0 ? (
        <CollapsedBin
          label={setAsideBinLabel(payload.suppressed.length)}
          openLabel={SET_ASIDE_BIN_OPEN_LABEL}
        >
          {payload.suppressed.map((entry) => (
            <div className="bin-row" key={entry.candidate_ref}>
              <div className="bname">{entry.name}</div>
              <div className="bdetail">{setAsideReason(entry)}</div>
            </div>
          ))}
        </CollapsedBin>
      ) : null}

      {samePerson.checked ? (
        <SamePersonFoot
          checked={samePerson.checked}
          total={payload.candidates.length}
          working={pending?.kind === 'same'}
          onCancel={samePerson.close}
          onConfirm={() => {
            const { refs, corrections } = samePerson.answer(
              payload.candidates.map((c) => c.candidate_ref),
            );
            submit(
              { kind: 'same' },
              {
                chosen_candidates: refs,
                ...(corrections.length > 0 ? { anchor_corrections: corrections } : {}),
              },
            );
          }}
        />
      ) : (
        <div className="layer-actions candidate-actions">
          <WorkingButton
            label="None of these"
            workingLabel="Saving…"
            variant="act"
            working={pending?.kind === 'none'}
            disabled={pending !== null && pending.kind !== 'none'}
            onClick={() => submit({ kind: 'none' }, { none_of_these: true })}
          />
          {payload.candidates.length > 1 ? (
            <SamePersonOpen disabled={pending !== null} onClick={samePerson.open} />
          ) : null}
        </div>
      )}
    </div>
  );
}
